/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { classNames } from '../utils/UNSAFE_classNames';
import { SortCriterionDetail } from '../PRIVATE_TableDisplay';
import { TableDisplayColumn } from './TableDisplayBase';
import { TableDisplayHeaderCell } from './TableDisplayHeaderCell';
import { backgroundStyles, tableHeaderRowStyles } from '../PRIVATE_Table/themes/Table.css';

/**
 * Props for the TableDisplayHeader Component
 */
export type TableDisplayHeaderProps<K, D, C> = {
  tableId: string;
  isRendered: boolean;
  columnsArray: TableDisplayColumn<K, D, C>[];
  hasVerticalGridlines: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  sortCriterion?: { key: C; sortDirection: 'ascending' | 'descending' }[];
  onSortCriterionChange?: (detail: SortCriterionDetail<C>) => void;
  stickyTopOffset?: number;
  startStickyEdge?: number;
  endStickyEdge?: number;
  firstStickyColumnIndex?: number;
  finalStickyColumnIndex?: number;
  columnBorderEdges: { hasStart: boolean; hasEnd: boolean; width: number }[];
};

/**
 * The internal component used to render a header region in Table.
 */
export function TableDisplayHeader<K, D, C>({
  tableId,
  isRendered,
  columnsArray,
  hasVerticalGridlines,
  sortCriterion,
  onSortCriterionChange,
  stickyTopOffset,
  startStickyEdge,
  endStickyEdge,
  firstStickyColumnIndex,
  finalStickyColumnIndex,
  columnBorderEdges,
  isRtl,
  isHighContrast
}: TableDisplayHeaderProps<K, D, C>) {
  const rowClasses = classNames([tableHeaderRowStyles.base]);

  return isRendered ? (
    <thead class={backgroundStyles.inherit} role={'presentation'}>
      <tr aria-rowindex={1} class={rowClasses} role={'row'} data-oj-table-header-row={tableId}>
        {columnsArray.map((column: TableDisplayColumn<K, D, C>, columnIndex: number) => {
          const columnKey = column.key;
          let sortDirection;
          if (sortCriterion != null) {
            for (const criterion of sortCriterion) {
              if (criterion.key === columnKey) {
                sortDirection = criterion.sortDirection;
                break;
              }
            }
          }
          const isSticky = column.value.sticky === 'enabled';
          let isFirstLogicalColumnIndex = false;
          let isFinalLogicalColumnIndex = false;
          if (isSticky) {
            if (startStickyEdge != null && firstStickyColumnIndex === columnIndex) {
              isFirstLogicalColumnIndex = true;
            }
            if (endStickyEdge != null && finalStickyColumnIndex === columnIndex) {
              isFinalLogicalColumnIndex = true;
            }
          }
          const headerCellProps = {
            tableId: tableId,
            hasVerticalGridlines: hasVerticalGridlines,
            columnKey: columnKey,
            columnIndex: columnIndex,
            isFinalColumnIndex: columnIndex === columnsArray.length - 1,
            isFirstLogicalColumnIndex: isFirstLogicalColumnIndex,
            isFinalLogicalColumnIndex: isFinalLogicalColumnIndex,
            stickyTopOffset: stickyTopOffset,
            isSticky: isSticky,
            hasStartEdge: columnBorderEdges[columnIndex].hasStart,
            hasEndEdge: columnBorderEdges[columnIndex].hasEnd,
            headerText: column.value.headerText,
            renderer: column.value.headerRenderer,
            sortable: column.value.sortable,
            sortDirection: sortDirection,
            onSortCriterionChange: onSortCriterionChange,
            isStickyStartEdge: columnIndex === startStickyEdge,
            isStickyEndEdge: columnIndex === endStickyEdge,
            isRtl: isRtl,
            isAccColumnHeader: column.value.accessibleColumnHeader !== 'disabled',
            isHighContrast: isHighContrast,
            padding: column.value.headerPadding,
            horizontalAlignment:
              column.value.headerAlignment?.horizontal != null
                ? column.value.headerAlignment.horizontal
                : column.value.horizontalAlignment,
            verticalAlignment: column.value.headerAlignment?.vertical
          };
          return <TableDisplayHeaderCell {...headerCellProps} />;
        })}
      </tr>
    </thead>
  ) : null;
}
